import { useEffect, useRef } from "react";

/**
 * Configuration for a query param to preserve across client-side navigations.
 */
export interface PreservedParam {
  /** The URL query param name (e.g., "impersonate", "org") */
  name: string;
  /** sessionStorage key used to bridge the value across navigations */
  storageKey: string;
}

const SESSION_PREFIX = "__preserved_param_";

/**
 * Preserves specified URL query params across client-side navigations.
 *
 * Patches window.history.pushState/replaceState to inject preserved params
 * into destination URLs. This catches all navigation paths (Next.js router,
 * <Link>, programmatic pushState) without relying on React lifecycle events.
 *
 * On full page load, the URL is the source of truth — if a param is absent,
 * its stale sessionStorage value is cleared. During client-side navigations,
 * sessionStorage bridges the value across pushState/replaceState calls.
 *
 * Must be called once at _app.tsx level so it runs before any navigation.
 *
 * @example
 * // In _app.tsx:
 * usePreserveQueryParams([
 *   { name: "impersonate", storageKey: "impersonate" },
 *   { name: "org", storageKey: "org" },
 * ]);
 */
export function usePreserveQueryParams(params: PreservedParam[]): void {
  // Use a ref so the patch closures always see the latest param list
  // without needing to re-register the patches on every render.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    // On full page load, URL is the source of truth.
    // Store present params; clear stale ones.
    const currentSearch = new URLSearchParams(window.location.search);
    for (const param of paramsRef.current) {
      const key = SESSION_PREFIX + param.storageKey;
      const value = currentSearch.get(param.name);
      if (value) {
        sessionStorage.setItem(key, value);
      } else {
        sessionStorage.removeItem(key);
      }
    }

    const origPushState = window.history.pushState.bind(window.history);
    const origReplaceState = window.history.replaceState.bind(window.history);

    function injectParams(
      url: string | URL | null | undefined,
    ): string | URL | null | undefined {
      if (!url) return url;

      try {
        const urlObj = new URL(
          typeof url === "string" ? url : url.toString(),
          window.location.origin,
        );
        let modified = false;
        for (const param of paramsRef.current) {
          if (!urlObj.searchParams.has(param.name)) {
            const stored = sessionStorage.getItem(
              SESSION_PREFIX + param.storageKey,
            );
            if (stored) {
              urlObj.searchParams.set(param.name, stored);
              modified = true;
            }
          }
        }
        if (modified) {
          return urlObj.pathname + urlObj.search + urlObj.hash;
        }
      } catch {
        // not a valid URL, pass through
      }
      return url;
    }

    function syncFromUrl(url: string | URL | null | undefined): void {
      if (!url) return;
      try {
        const urlObj = new URL(
          typeof url === "string" ? url : url.toString(),
          window.location.origin,
        );
        for (const param of paramsRef.current) {
          const val = urlObj.searchParams.get(param.name);
          if (val) {
            sessionStorage.setItem(SESSION_PREFIX + param.storageKey, val);
          }
        }
      } catch {
        // ignore
      }
    }

    window.history.pushState = function (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any,
      unused: string,
      url?: string | URL | null,
    ) {
      return origPushState(data, unused, injectParams(url));
    };

    window.history.replaceState = function (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any,
      unused: string,
      url?: string | URL | null,
    ) {
      // If the URL explicitly sets any of our params, sync to sessionStorage
      // and pass through without injection (avoid overwriting explicit values).
      syncFromUrl(url);
      return origReplaceState(data, unused, injectParams(url));
    };

    return () => {
      window.history.pushState = origPushState;
      window.history.replaceState = origReplaceState;
    };
  }, []);
}

/**
 * Clear a preserved param from sessionStorage.
 * Call when the param should no longer be preserved (e.g., stopping impersonation).
 */
export function clearPreservedParam(storageKey: string): void {
  sessionStorage.removeItem(SESSION_PREFIX + storageKey);
}
